
import { useCallback, useEffect } from "react";

interface CollapseState {
  [exerciseId: string]: boolean;
}

const COLLAPSE_STATE_KEY = 'exercise-collapse-state';

export const useExerciseCollapseState = () => {
  // Función para obtener el estado desde localStorage
  const getStoredState = useCallback((): CollapseState => {
    try {
      const stored = localStorage.getItem(COLLAPSE_STATE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn("Error reading collapse state from localStorage:", error);
      return {};
    }
  }, []);

  // Función para guardar el estado en localStorage
  const saveState = useCallback((state: CollapseState) => {
    try {
      localStorage.setItem(COLLAPSE_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn("Error saving collapse state to localStorage:", error);
    }
  }, []);

  // Generar clave única para ejercicio usando routineId + exerciseId
  const getStateKey = useCallback((routineId: string, exerciseId: string) => {
    return `${routineId}:${exerciseId}`;
  }, []);

  const getCollapsedState = useCallback((routineId: string, exerciseId: string): boolean => {
    const storedState = getStoredState();
    const key = getStateKey(routineId, exerciseId);
    return storedState[key] || false;
  }, [getStoredState, getStateKey]);

  const updateCollapsedState = useCallback((routineId: string, exerciseId: string, collapsed: boolean) => {
    const storedState = getStoredState();
    const key = getStateKey(routineId, exerciseId);
    
    const newState = {
      ...storedState,
      [key]: collapsed
    };
    
    saveState(newState);
    
    console.log(`💾 Persisted collapse state: ${key} = ${collapsed}`);
  }, [getStoredState, saveState, getStateKey]);

  // Preservar estados para drag (crear snapshot temporal)
  const preserveStatesForDrag = useCallback((routineId: string, exerciseIds: string[]) => {
    const storedState = getStoredState();
    const preservedStates: CollapseState = {};
    
    exerciseIds.forEach(exerciseId => {
      const key = getStateKey(routineId, exerciseId);
      preservedStates[key] = storedState[key] || false;
    });
    
    console.log("📋 Preserved collapse states for drag:", preservedStates);
    return preservedStates;
  }, [getStoredState, getStateKey]);

  // Restaurar estados después de drag (no necesario ya que usamos localStorage)
  const restoreStatesFromDrag = useCallback((preservedStates?: CollapseState) => {
    // Con localStorage no necesitamos restaurar, pero mantenemos la función por compatibilidad
    if (preservedStates) {
      console.log("🔄 States are already persisted in localStorage, no restoration needed");
    }
    return preservedStates;
  }, []);

  // Limpiar estados de ejercicios que ya no existen
  const cleanupStaleStates = useCallback((routineId: string, currentExerciseIds: string[]) => {
    const storedState = getStoredState();
    const currentKeys = new Set(currentExerciseIds.map(id => getStateKey(routineId, id)));
    
    // Encontrar claves obsoletas para esta rutina
    const staleKeys = Object.keys(storedState).filter(key => 
      key.startsWith(`${routineId}:`) && !currentKeys.has(key)
    );
    
    if (staleKeys.length > 0) {
      const cleanedState = { ...storedState };
      staleKeys.forEach(key => delete cleanedState[key]);
      saveState(cleanedState);
      
      console.log(`🧹 Cleaned up ${staleKeys.length} stale collapse states for routine ${routineId}`);
    }
  }, [getStoredState, saveState, getStateKey]);

  // Limpiar todos los estados de una rutina específica
  const clearRoutineStates = useCallback((routineId: string) => {
    const storedState = getStoredState();
    const cleanedState = Object.fromEntries(
      Object.entries(storedState).filter(([key]) => !key.startsWith(`${routineId}:`))
    );
    
    saveState(cleanedState);
    console.log(`🗑️ Cleared all collapse states for routine ${routineId}`);
  }, [getStoredState, saveState]);

  return {
    getCollapsedState,
    updateCollapsedState,
    preserveStatesForDrag,
    restoreStatesFromDrag,
    cleanupStaleStates,
    clearRoutineStates
  };
};
